<div class="invoice-progress-line col full-width">
  <hmt-progress-tab-line [steps]="steps" [currentStep]="(invoice$ | async)?.progressStep"></hmt-progress-tab-line>

  <ng-container *ngIf="(invoice$ | async) as invoice">
    <ng-template #inlineEditorTpl>
      <form class="tr row inline-editor-row" [formGroup]="inlineForm" (ngSubmit)="saveInline()">
        <div class="td col-no"></div>
        <div class="td col-desc">
          <input class="ie-input" type="text" placeholder="Enter description"
                 formControlName="description"
                 *ngIf="showsField('description')">
          <mat-form-field appearance="outline" class="ie-tax-select"
                          *ngIf="showsField('taxName')">
            <mat-select formControlName="taxName" placeholder="Select tax"
                        (selectionChange)="onTaxNameChange($event.value)">
              <mat-option *ngFor="let t of taxList" [value]="t.name">{{ t.name }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <div class="td col-amount inline-amount">
          <span class="pct-field" *ngIf="showsField('percentage')">
            <input type="number" placeholder="0" formControlName="percentage" min="0">
            <span class="suffix">%</span>
          </span>
          <span class="amt-field" *ngIf="showsField('amount')">
            <span class="prefix">{{ invoice.currency.currentCurrency }}</span>
            <input type="number" placeholder="0.00" formControlName="amount" min="0">
          </span>
          <button type="submit" mat-icon-button class="ie-icon save"
                  aria-label="Save">
            <mat-icon>save</mat-icon>
          </button>
          <button type="button" mat-icon-button class="ie-icon cancel"
                  (click)="cancelInline()" aria-label="Cancel">
            <mat-icon>delete_outline</mat-icon>
          </button>
        </div>
      </form>
    </ng-template>

    <div class="invoice-shell">
      <div class="invoice-card">
        <!-- ===== Top toolbar : back, invoice ref, download ===== -->
        <div class="inv-toolbar row">
          <div class="left row">
            <button mat-icon-button class="back-btn" (click)="backToInvoicePage()" aria-label="Back">
              <mat-icon>chevron_left</mat-icon>
            </button>
            <div class="inv-ref-chip">{{ invoice.invoiceRefNo }}</div>
          </div>
          <div class="right row">
            <button mat-button class="download-link" (click)="downloadPdf()">
              <mat-icon>file_download</mat-icon>
              Download Invoice
            </button>
            <button mat-button class="download-link"
                    *ngIf="openedFromSentInvoices"
                    (click)="openChat()"
                    [disabled]="!isChatEligible(invoice)"
                    [attr.title]="isChatEligible(invoice) ? null : 'Chat unavailable for this invoice'">
              <mat-icon>chat_bubble_outline</mat-icon>
              Chat
            </button>
            <button mat-icon-button class="refresh-btn" (click)="onSaveInvoice()" aria-label="Refresh">
              <mat-icon>refresh</mat-icon>
            </button>
          </div>
        </div>

        <!-- ===== Supplier header + Total Amount ===== -->
        <div class="supplier-header row"
             *ngIf="(orgDetails$ | async)[invoice.serviceProvider] as serviceProvider">
          <div class="supplier-info row">
            <div class="supplier-logo">
              <img *ngIf="serviceProvider.logo" [src]="serviceProvider.logo" alt="logo">
            </div>
            <div class="supplier-text col">
              <p class="org-name">{{ serviceProvider.orgName }}</p>
              <!-- TODO: contact person field not in OrgDetails -->
              <p class="muted" *ngIf="serviceProvider.address?.addressLine">
                {{ serviceProvider.address.addressLine }}
              </p>
              <p class="muted">
                <ng-container *ngIf="serviceProvider.address?.city">{{ serviceProvider.address.city }}</ng-container><ng-container *ngIf="serviceProvider.address?.country">, {{ serviceProvider.address.country }}</ng-container>
              </p>
              <p class="muted" *ngIf="serviceProvider.companyRegNum">SIRET: {{ serviceProvider.companyRegNum }}</p>
              <p class="muted" *ngIf="serviceProvider.suspendedVat">VAT: {{ serviceProvider.suspendedVat }}</p>
            </div>
          </div>
          <div class="supplier-total col">
            <p class="total-label">Total Amount</p>
            <p class="total-value">{{ applyCurrency(getTotal()) }}</p>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- ===== Side info card + Billing To + Note ===== -->
        <div class="meta-row row">
          <!-- Left: side info card -->
          <div class="side-info col">
            <div class="info-line">
              <span class="info-label">Created Date</span>
              <span class="info-value">{{ invoice.createdAt | date:'MM/dd/yyyy' }}</span>
            </div>
            <div class="info-line">
              <span class="info-label">Delivery Date</span>
              <!-- TODO: deliveryDate not in invoice model; falling back to createdAt -->
              <span class="info-value">{{ invoice.createdAt | date:'MM/dd/yyyy' }}</span>
            </div>
            <div class="info-line">
              <span class="info-label">Terms of Payment</span>
              <span class="info-value">{{ invoice.invoicePaymentTermStr || '—' }}</span>
            </div>
            <div class="info-line payment-deadline">
              <span class="info-label">Payment Deadline</span>
              <span class="info-value">
                <!-- TODO: paymentDeadline not in model; placeholder -->
                {{ invoice.createdAt | date:'MM/dd/yyyy' }}
                <mat-icon class="edit-pencil" *ngIf="checkPermission(invoice)">edit</mat-icon>
              </span>
            </div>
          </div>

          <!-- Right: Billing To + Note -->
          <div class="billing-block col">
            <div class="billing-head row" *ngIf="(orgDetails$ | async)[invoice.createdTo] as customer">
              <div class="customer-text col">
                <p class="billing-title">Billing To</p>
                <p class="customer-name">{{ customer.orgName }}</p>
                <p class="muted" *ngIf="customer.address?.addressLine">{{ customer.address.addressLine }}</p>
                <p class="muted">
                  <ng-container *ngIf="customer.address?.city">{{ customer.address.city }}</ng-container><ng-container *ngIf="customer.address?.country">, {{ customer.address.country }}</ng-container>
                </p>
                <p class="muted" *ngIf="customer.companyRegNum">SIRET: {{ customer.companyRegNum }}</p>
                <p class="muted" *ngIf="customer.suspendedVat">VAT: {{ customer.suspendedVat }}</p>
              </div>
              <div class="customer-logo">
                <img *ngIf="customer.logo" [src]="customer.logo" alt="logo">
              </div>
            </div>

          </div>
        </div>

        <!-- ===== Job Details ===== -->
        <div class="section job-details-section">
          <p class="section-title">Job Details:</p>
          <div class="table">
            <div class="thead row">
              <div class="th col-jobtitle">Job Title</div>
              <div class="th col-internal">Internal Ref Numbers</div>
              <div class="th col-contract">Contract Reference Number</div>
            </div>
            <div class="tbody">
              <div class="tr row"
                   *ngFor="let jobTitle of invoice.jobTitles; let i = index">
                <div class="td col-jobtitle">{{ jobTitle }}</div>
                <div class="td col-internal">{{ invoice.internalRefNos[i] || invoice.internalRefNos[0] || '—' }}</div>
                <div class="td col-contract">{{ invoice.contractRefNos[i] || invoice.contractRefNos[0] || '—' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== Services & Charges ===== -->
        <div class="section services-section">
          <div class="section-head row">
            <p class="section-title">Services &amp; Charges</p>
            <button mat-button class="action-link"
                    (click)="openAddLoadDialog()"
                    [disabled]="!checkPermission(invoice)">
              + Add Load
            </button>
          </div>

          <div class="table loads-table">
            <div class="thead row">
              <div class="th col-no">NO.</div>
              <div class="th col-desc">DESCRIPTION</div>
              <div class="th col-amount">AMOUNT</div>
            </div>
            <div class="tbody">
              <ng-container *ngFor="let load of invoice.loads; let idx = index">
                <div class="tr row load-row"
                     (click)="toggleLoadExpand(load.loadId)"
                     style="cursor: pointer">
                  <div class="td col-no">
                    <mat-icon class="chev">
                      {{ isLoadExpanded(load.loadId) ? 'expand_more' : 'chevron_right' }}
                    </mat-icon>
                    {{ idx + 1 }}
                  </div>
                  <div class="td col-desc">
                    <div class="load-title">
                      {{ (load.loadNumber == null || load.loadNumber === '') ? load.loadId : load.loadNumber }}
                    </div>
                    <div class="load-sub muted">Job ID - {{ load.loadId }}</div>
                  </div>
                  <div class="td col-amount">
                    <span class="amt">{{ applyCurrency(calculateLoadTotal(load)) }}</span>
                    <mat-icon class="row-action danger"
                              *ngIf="checkPermission(invoice)"
                              (click)="removeLoad(load._id, invoice._id); $event.stopPropagation()">delete</mat-icon>
                  </div>
                </div>

                <div class="locations-strip row" *ngIf="load.locations?.length">
                  <ng-container *ngFor="let location of load.locations; let i = index">
                    <span class="location-chip">{{ location }}</span>
                    <mat-icon class="loc-arrow" *ngIf="i < load.locations.length - 1">double_arrow</mat-icon>
                  </ng-container>
                </div>

                <!-- Expanded breakdown (only when this load is expanded) -->
                <ng-container *ngIf="isLoadExpanded(load.loadId)">
                  <div class="tr row sub-group-row" *ngIf="checkPermission(invoice)">
                    <div class="td col-no"></div>
                    <div class="td col-desc"></div>
                    <div class="td col-amount">
                      <button mat-button class="action-link"
                              type="button"
                              *ngIf="!isInlineActive('load-fee','add', load.loadId)"
                              (click)="addCost(load.loadId, null)"
                              [disabled]="!checkPermission(invoice)">
                        + Add Fee
                      </button>
                    </div>
                  </div>
                  <ng-container *ngIf="isInlineActive('load-fee','add', load.loadId)">
                    <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                  </ng-container>
                  <div class="tr row inner-row">
                    <div class="td col-no"></div>
                    <div class="td col-desc">
                      Transportation Fee
                      <div class="load-sub muted" *ngIf="load.rateSheetType === 'WEIGHT_BASED'">
                        {{ load.weightInLoadKg }} kg &rarr; {{ load.weightInBillingUnit | number:'1.0-2' }} {{ load.rateUnit | lowercase }}
                        &times; {{ applyCurrency(load.ratePerUnit || 0) }}/{{ load.rateUnit | lowercase }}
                      </div>
                    </div>
                    <div class="td col-amount">
                      <span class="amt">{{ applyCurrency(load.loadCost) }}</span>
                    </div>
                  </div>
                  <ng-container *ngFor="let cost of load?.costs">
                    <div class="tr row inner-row" *ngIf="!isInlineActive('load-fee','edit', load.loadId, cost.id)">
                      <div class="td col-no"></div>
                      <div class="td col-desc">{{ cost.description }}</div>
                      <div class="td col-amount">
                        <span class="amt">{{ applyCurrency(cost.value) }}</span>
                        <mat-icon class="row-action" *ngIf="checkPermission(invoice)" (click)="addCost(load.loadId, cost)">edit</mat-icon>
                        <mat-icon class="row-action danger" *ngIf="checkPermission(invoice)" (click)="removeCost(cost.id, load.loadId)">delete_outline</mat-icon>
                      </div>
                    </div>
                    <ng-container *ngIf="isInlineActive('load-fee','edit', load.loadId, cost.id)">
                      <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-container *ngFor="let cost of load?.addedAdditionalCost">
                    <div class="tr row inner-row" *ngIf="!isInlineActive('load-additional-cost','edit', load.loadId, cost.id)">
                      <div class="td col-no"></div>
                      <div class="td col-desc">{{ cost.description }}</div>
                      <div class="td col-amount">
                        <span class="amt">{{ applyCurrency(cost.value) }}</span>
                        <mat-icon class="row-action" *ngIf="checkPermission(invoice)" (click)="addLoadAdditionalCost(load.loadId, cost)">edit</mat-icon>
                        <mat-icon class="row-action danger" *ngIf="checkPermission(invoice)" (click)="removeLoadAdditionalCost(cost.id, load.loadId)">delete_outline</mat-icon>
                      </div>
                    </div>
                    <ng-container *ngIf="isInlineActive('load-additional-cost','edit', load.loadId, cost.id)">
                      <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-container *ngFor="let commission of load?.commissions">
                    <ng-container *ngFor="let feetype of commission.feeType">
                      <div class="tr row inner-row"
                           *ngIf="calculateCommisions(commission, load, feetype) > 0
                               && !isInlineActive('load-commission','edit', load.loadId, commission.id)">
                        <div class="td col-no"></div>
                        <div class="td col-desc">{{ commission.description }} — {{ feetype }} ({{ commission.value }}%)</div>
                        <div class="td col-amount">
                          <span class="amt">{{ applyCurrency(calculateCommisions(commission, load, feetype)) }}</span>
                          <mat-icon class="row-action" *ngIf="checkPermission(invoice)" (click)="editCommission(commission, load.loadId)">edit</mat-icon>
                          <mat-icon class="row-action danger" *ngIf="checkPermission(invoice)" (click)="removeCommission(commission, feetype, load.loadId)">delete_outline</mat-icon>
                        </div>
                      </div>
                    </ng-container>
                    <ng-container *ngIf="isInlineActive('load-commission','edit', load.loadId, commission.id)">
                      <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-container *ngIf="(load?.addedOtherCost?.length || 0) > 0 || checkPermission(invoice)">
                    <div class="tr row sub-group-row">
                      <div class="td col-no"></div>
                      <div class="td col-desc sub-group-title">Additional fee</div>
                      <div class="td col-amount">
                        <button mat-button class="action-link"
                                type="button"
                                *ngIf="checkPermission(invoice) && !isInlineActive('load-item','add', load.loadId)"
                                (click)="openLoadAddItem(load.loadId, null)"
                                [disabled]="!checkPermission(invoice)">
                          + Add Item
                        </button>
                      </div>
                    </div>
                    <ng-container *ngIf="isInlineActive('load-item','add', load.loadId)">
                      <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                    </ng-container>
                    <ng-container *ngFor="let lineItem of load?.addedOtherCost">
                      <div class="tr row inner-row" *ngIf="!isInlineActive('load-item','edit', load.loadId, lineItem.id)">
                        <div class="td col-no"></div>
                        <div class="td col-desc">{{ lineItem.description }}</div>
                        <div class="td col-amount">
                          <span class="pct" *ngIf="lineItem.paymentType === paymentType.PERCENTAGE">{{ lineItem.value }} %</span>
                          <span class="amt" *ngIf="lineItem.paymentType === paymentType.AMOUNT || !lineItem.paymentType">
                            {{ applyCurrency(lineItem.value) }}
                          </span>
                          <span class="amt" *ngIf="lineItem.paymentType === paymentType.PERCENTAGE">
                            {{ applyCurrency(calculateLineItemValue(lineItem.value)) }}
                          </span>
                          <mat-icon class="row-action" *ngIf="checkPermission(invoice)"
                                    (click)="openLoadAddItem(load.loadId, lineItem)">edit</mat-icon>
                          <mat-icon class="row-action danger" *ngIf="checkPermission(invoice)"
                                    (click)="removeLoadOtherCost(lineItem.id, load.loadId)">delete_outline</mat-icon>
                        </div>
                      </div>
                      <ng-container *ngIf="isInlineActive('load-item','edit', load.loadId, lineItem.id)">
                        <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                      </ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-container *ngIf="load?.addedDiscount != null || checkPermission(invoice)">
                    <div class="tr row sub-group-row">
                      <div class="td col-no"></div>
                      <div class="td col-desc sub-group-title">Discount</div>
                      <div class="td col-amount">
                        <button mat-button class="action-link"
                                type="button"
                                *ngIf="checkPermission(invoice) && load?.addedDiscount == null
                                     && !isInlineActive('load-discount','add', load.loadId)"
                                (click)="openLoadAddDiscount(load.loadId, null)"
                                [disabled]="!checkPermission(invoice)">
                          + Add Discount
                        </button>
                      </div>
                    </div>
                    <ng-container *ngIf="isInlineActive('load-discount','add', load.loadId)">
                      <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                    </ng-container>
                    <div class="tr row inner-row"
                         *ngIf="load?.addedDiscount != null
                             && !isInlineActive('load-discount','edit', load.loadId, load.addedDiscount.id)">
                      <div class="td col-no"></div>
                      <div class="td col-desc">
                        {{ load.addedDiscount.description || 'Discount' }}
                      </div>
                      <div class="td col-amount">
                        <span class="pct" *ngIf="load.addedDiscount.paymentType === paymentType.PERCENTAGE">
                          {{ load.addedDiscount.value }} %
                        </span>
                        <span class="amt discount" *ngIf="load.addedDiscount.paymentType === paymentType.AMOUNT">
                          - {{ applyCurrency(load.addedDiscount.value) }}
                        </span>
                        <span class="amt discount" *ngIf="load.addedDiscount.paymentType === paymentType.PERCENTAGE">
                          - {{ applyCurrency(calculateAddedDiscountTotal(load)) }}
                        </span>
                        <mat-icon class="row-action" *ngIf="checkPermission(invoice)"
                                  (click)="openLoadAddDiscount(load.loadId, load.addedDiscount)">edit</mat-icon>
                        <mat-icon class="row-action danger" *ngIf="checkPermission(invoice)"
                                  (click)="removeLoadDiscount(load.loadId)">delete_outline</mat-icon>
                      </div>
                    </div>
                    <ng-container *ngIf="load?.addedDiscount != null
                                     && isInlineActive('load-discount','edit', load.loadId, load.addedDiscount.id)">
                      <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                    </ng-container>
                  </ng-container>
                </ng-container>
              </ng-container>
            </div>
          </div>
        </div>

        <!-- ===== Additional Charges ===== -->
        <div class="section additional-section">
          <div class="section-head row">
            <p class="section-title">Additional Charges</p>
            <button mat-button class="action-link"
                    type="button"
                    *ngIf="!isInlineActive('invoice-additional','add')"
                    (click)="addAdditionalCost()"
                    [disabled]="!checkPermission(invoice)">
              + Add Item
            </button>
          </div>

          <div class="table">
            <div class="thead row">
              <div class="th col-no">NO.</div>
              <div class="th col-desc">DESCRIPTION</div>
              <div class="th col-amount">AMOUNT</div>
            </div>
            <div class="tbody">
              <ng-container *ngIf="isInlineActive('invoice-additional','add')">
                <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
              </ng-container>
              <div class="tr row"
                   *ngFor="let cost of invoice.additionalCosts; let i = index">
                <div class="td col-no">{{ i + 1 }}</div>
                <div class="td col-desc">{{ cost.description }}</div>
                <div class="td col-amount">
                  <span class="amt">{{ applyCurrency(cost.value) }}</span>
                  <mat-icon class="row-action danger"
                            *ngIf="checkPermission(invoice)"
                            (click)="removeAdditionalCost(cost.id)">delete_outline</mat-icon>
                </div>
              </div>
              <div class="tr row subtotal-row">
                <div class="td col-no"></div>
                <div class="td col-desc subtotal-label">Subtotal</div>
                <div class="td col-amount">
                  <span class="amt">{{ applyCurrency(calculateServiceFee()) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== Other (line items + discount) ===== -->
        <div class="section other-section">
          <div class="section-head row">
            <p class="section-title">Other</p>
            <button mat-button class="action-link"
                    type="button"
                    *ngIf="!isInlineActive('invoice-item','add')"
                    (click)="openAddItem()"
                    [disabled]="!checkPermission(invoice)">
              + Add Item
            </button>
          </div>

          <div class="table">
            <div class="thead row">
              <div class="th col-no">NO.</div>
              <div class="th col-desc">DESCRIPTION</div>
              <div class="th col-amount">AMOUNT</div>
            </div>
            <div class="tbody">
              <ng-container *ngIf="isInlineActive('invoice-item','add')">
                <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
              </ng-container>
              <div class="tr row"
                   *ngFor="let lineItem of invoice.otherCosts; let i = index">
                <div class="td col-no">{{ i + 1 }}</div>
                <div class="td col-desc">{{ lineItem.description }}</div>
                <div class="td col-amount">
                  <span class="pct" *ngIf="lineItem.paymentType === paymentType.PERCENTAGE">{{ lineItem.value }} %</span>
                  <span class="amt"
                        *ngIf="lineItem.paymentType === paymentType.AMOUNT || !lineItem.paymentType">{{ applyCurrency(lineItem.value) }}</span>
                  <span class="amt"
                        *ngIf="lineItem.paymentType === paymentType.PERCENTAGE">{{ applyCurrency(calculateLineItemValue(lineItem.value)) }}</span>
                  <mat-icon class="row-action danger"
                            *ngIf="checkPermission(invoice)"
                            (click)="removeLineItem(lineItem)">delete_outline</mat-icon>
                </div>
              </div>

              <div class="tr row"
                   *ngIf="invoice.discount != null
                       && !isInlineActive('invoice-discount','edit', undefined, invoice.discount.id)">
                <div class="td col-no">{{ (invoice.otherCosts?.length || 0) + 1 }}</div>
                <div class="td col-desc">{{ invoice.discount.description || 'Discount' }}</div>
                <div class="td col-amount">
                  <span class="pct"
                        *ngIf="invoice.discount.paymentType === paymentType.PERCENTAGE">{{ invoice.discount.value }} %</span>
                  <span class="amt"
                        *ngIf="invoice.discount.paymentType === paymentType.AMOUNT">{{ applyCurrency(invoice.discount.value) }}</span>
                  <span class="amt"
                        *ngIf="invoice.discount.paymentType === paymentType.PERCENTAGE">{{ applyCurrency(calculateDiscount()) }}</span>
                  <mat-icon class="row-action"
                            *ngIf="checkPermission(invoice)"
                            (click)="editDiscount(invoice.discount)">edit</mat-icon>
                  <mat-icon class="row-action danger"
                            *ngIf="checkPermission(invoice)"
                            (click)="removeDiscount()">delete_outline</mat-icon>
                </div>
              </div>
              <ng-container *ngIf="invoice.discount != null
                              && isInlineActive('invoice-discount','edit', undefined, invoice.discount.id)">
                <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
              </ng-container>

              <ng-container *ngIf="isInlineActive('invoice-discount','add')">
                <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
              </ng-container>

              <div class="add-discount-row row"
                   *ngIf="invoice.discount == null
                       && checkPermission(invoice)
                       && !isInlineActive('invoice-discount','add')">
                <button mat-button class="action-link" type="button" (click)="openAddDiscount()">+ Add Discount</button>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== Tax section (kept from previous logic, in compact form) ===== -->
        <div class="section tax-section" *ngIf="(invoice.taxes?.length || 0) > 0 || checkPermission(invoice)">
          <div class="section-head row">
            <p class="section-title">Tax</p>
            <button mat-button class="action-link"
                    type="button"
                    *ngIf="!isInlineActive('invoice-tax','add')"
                    (click)="openAddTax()"
                    [disabled]="!checkPermission(invoice)">
              + Add Tax
            </button>
          </div>
          <mat-checkbox class="svat-checkbox"
                        *ngIf="(orgDetails$ | async)[invoice.createdTo]?.suspendedVat != null
                            && (orgDetails$ | async)[invoice.serviceProvider]?.suspendedVat != null"
                        (change)="onChangeSVat($event)"
                        [checked]="invoice.suspendedVat">
            Mark SVAT
          </mat-checkbox>
          <div class="table" *ngIf="(invoice.taxes?.length || 0) > 0 || isInlineActive('invoice-tax','add')">
            <div class="tbody">
              <ng-container *ngIf="isInlineActive('invoice-tax','add')">
                <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
              </ng-container>
              <ng-container *ngFor="let tax of taxes; let i = index">
                <div class="tr row"
                     *ngIf="(tax.description !== 'Vat' || !suspendedVat)
                         && !isInlineActive('invoice-tax','edit', undefined, tax.id)">
                  <div class="td col-no">{{ i + 1 }}</div>
                  <div class="td col-desc">{{ tax.description }}</div>
                  <div class="td col-amount">
                    <span class="pct" *ngIf="tax.paymentType === paymentType.PERCENTAGE">{{ tax.value }} %</span>
                    <span class="amt">{{ applyCurrency(taxAmounts[i]) }}</span>
                    <mat-icon class="row-action" *ngIf="checkPermission(invoice)" (click)="openEditTax(tax)">edit</mat-icon>
                    <mat-icon class="row-action danger" *ngIf="checkPermission(invoice)" (click)="removeTax(tax.id)">delete_outline</mat-icon>
                  </div>
                </div>
                <ng-container *ngIf="isInlineActive('invoice-tax','edit', undefined, tax.id)">
                  <ng-container *ngTemplateOutlet="inlineEditorTpl"></ng-container>
                </ng-container>
              </ng-container>
            </div>
          </div>
          <p class="svat-amount" *ngIf="suspendedVat">SVAT: {{ applyCurrency(calculateSVat()) }}</p>
        </div>

        <!-- ===== Total Amount bar ===== -->
        <div class="total-bar row">
          <p class="total-bar-label">Total Amount</p>
          <p class="total-bar-amount">{{ applyCurrency(getTotal()) }}</p>
        </div>
        <p class="total-text">
          {{ applyCurrencyWithoutCurrencyCode(getTotal()) | numberToText : invoice.currency.currentCurrency }}
        </p>

        <!-- ===== Currency exchange (existing logic) ===== -->
        <div class="currency-row row" *ngIf="invoice.defaultOrgCurrencyCode !== invoice.currencyCode">
          <p [ngClass]="invoice.exchangeRate == 0 ? 'font-small-bold-red' : 'font-small-bold'">
            Exchange Rate: 1 {{ invoice.currencyCode }} =
          </p>
          <form [formGroup]="rateForm">
            <span class="spam-input"><input type="number" formControlName="rate"></span>
          </form>
          <p [ngClass]="invoice.exchangeRate == 0 ? 'font-small-bold-red' : 'font-small-bold'">
            {{ invoice.defaultOrgCurrencyCode }}
          </p>
        </div>

        <!-- ===== Select Payment Term ===== -->
        <div class="section payment-term-section">
          <p class="section-title">Select Payment Term</p>
          <div class="payment-term-host">
            <hmt-payment-terms
              (paymentTerm)="addPaymentTerm($event)"
              [selectedPaymentTerm]="invoice.invoicePaymentTerm"
              [totalCost]="getTotal() / invoice.currency.currencyRate">
            </hmt-payment-terms>
          </div>
        </div>

        <!-- ===== Terms & Conditions (kept) ===== -->
        <div class="section terms-section" *ngIf="invoice.loads?.length">
          <p class="section-title">Terms and Conditions</p>
          <div class="tac-e-panel">
            <div class="load" *ngFor="let load of invoice.loads">
              <mat-divider></mat-divider><br>
              <div class="content col">
                <div class="container-ids">
                  <p class="title">Load ID:</p>
                  <p>{{ load.loadId }}</p>
                </div>
                <ng-container *ngFor="let category of load.vehicleCategories">
                  <ng-container *ngIf="(rateCardsMap$ | async)[load.rateCard] as rateCard">
                    <p class="font-large-bold">Rate: {{ invoiceExtraService.getVehicleCategoryName(rateCard, category) }}</p>
                    <div class="rate-sheet"
                         *ngFor="let rateRow of invoiceExtraService.generateRateRows(rateCard, invoice.currency, category)">
                      <div class="col">
                        <div *ngFor="let rate of rateRow">
                          <p>{{ rate?.name }}: {{ rate?.value }}</p>
                        </div>
                      </div>
                    </div>
                    <p class="font-large-bold">Demurrage Slabs</p>
                    <div class="rate-sheet">
                      <div class="col">
                        <div *ngFor="let slab of invoiceExtraService.getDemurrageSlabs(rateCard, category); let i = index">
                          <p *ngIf="i === 0"> First {{ slab.hours }} Hrs: {{ slab.rate }} {{ invoice.currency.currentCurrency }} (Per Hr)</p>
                          <p *ngIf="i !== 0"> Next {{ slab.hours }} Hrs: {{ slab.rate }} {{ invoice.currency.currentCurrency }} (Per Hr)</p>
                        </div>
                      </div>
                    </div>
                    <p>Until Clearance: {{ rateCard.demurragePerHrTillClearance }} {{ invoice.currency.currentCurrency }} (Per hr)</p>
                    <br>
                  </ng-container>
                </ng-container>
              </div>
            </div>
          </div>
        </div>

        <!-- Expand / collapse toggle for the services breakdown -->
        <div class="expand-toggle row">
          <mat-slide-toggle [labelPosition]="'before'" (change)="toggleLoads()" [checked]="toggled">
            {{ toggled ? 'Collapse breakdown' : 'Expand breakdown' }}
          </mat-slide-toggle>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ===== Action footer (floating snackbar) ===== -->
  <div class="progress-line-footer" *ngIf="(invoice$ | async) as invoice">
    <div class="footer-total">
      <span class="footer-total-label">Total value</span>
      <span class="footer-total-value">{{ applyCurrency(getTotal()) }}</span>
    </div>
    <div class="footer-actions">
      <button mat-button class="fb-btn fb-secondary" (click)="backToInvoicePage()">Back</button>
      <button mat-button class="fb-btn fb-secondary"
              *ngIf="invoice.progressStep === progressLineStep.DRAFT
                  || invoice.progressStep === progressLineStep.SENT_FOR_INTERNAL_APPROVAL
                  || invoice.progressStep === progressLineStep.INTERNAL_APPROVED
                  || invoice.approvalState === approvalStep.DISPUTED"
              (click)="onSaveInvoice()">
        <mat-icon>save</mat-icon>SAVE
      </button>
      <button mat-button class="fb-btn fb-primary"
              *ngIf="invoice.approvalState === approvalStep.DISPUTED"
              (click)="onSaveAndResubmit()">
        <mat-icon>send</mat-icon>SAVE &amp; RESUBMIT
      </button>
      <ng-container *ngIf="invoice.approvalState !== approvalStep.DISPUTED">
        <button mat-button class="fb-btn fb-primary" [disabled]="checkSavedOrNot()"
                *ngIf="invoice.progressStep === progressLineStep.DRAFT
                    && invoice.approvalState !== approvalStep.SEND_FOR_INTERNAL_APPROVAL
                    && invoice.invoicePaymentTerm != null
                    && invoice.exchangeRate > 0"
                (click)="onSubmitForApproval()">
          <mat-icon>check_circle_outline</mat-icon>SUBMIT FOR APPROVAL
        </button>
        <button mat-button class="fb-btn fb-danger" [disabled]="checkSavedOrNot()"
                *ngIf="invoice.approvalState === approvalStep.SEND_FOR_INTERNAL_APPROVAL"
                (click)="onInternalReject()">
          <mat-icon>cancel</mat-icon>REJECT
        </button>
        <button mat-button class="fb-btn fb-primary" [disabled]="checkSavedOrNot()"
                *ngIf="invoice.approvalState === approvalStep.SEND_FOR_INTERNAL_APPROVAL"
                (click)="onInternalApprove()">
          <mat-icon>check_circle_outline</mat-icon>APPROVE
        </button>
        <button mat-button class="fb-btn fb-secondary" [disabled]="checkSavedOrNot()"
                *ngIf="invoice.progressStep === progressLineStep.INTERNAL_APPROVED
                    || invoice.progressStep === progressLineStep.SENT
                    || invoice.progressStep === progressLineStep.PAID"
                (click)="downloadPdf()">
          <mat-icon>file_download</mat-icon>DOWNLOAD
        </button>
        <button mat-button class="fb-btn fb-primary" [disabled]="checkSavedOrNot()"
                *ngIf="invoice.progressStep === progressLineStep.INTERNAL_APPROVED"
                (click)="sentToCustomer()">
          <mat-icon>send</mat-icon>SEND
        </button>
        <button mat-button class="fb-btn fb-primary"
                *ngIf="invoice.progressStep === progressLineStep.SENT
                    && invoice.approvalState === approvalStep.CUSTOMER_APPROVED
                    && invoice.overdueState === overdueStep.DEFAULT"
                (click)="markAsOverdue()">
          <mat-icon>schedule</mat-icon>MARK AS OVERDUE
        </button>
        <!-- <button mat-button class="fb-btn fb-primary"
                *ngIf="invoice.approvalState === approvalStep.CUSTOMER_APPROVED
                    && (invoice.overdueState === overdueStep.OVERDUE || invoice.overdueState === overdueStep.DEFAULT)"
                (click)="markAsPaid()">
          <mat-icon>check_circle_outline</mat-icon>MARK AS PAID
        </button> -->
        <!-- <button mat-button class="fb-btn fb-danger"
                *ngIf="invoice.progressStep === progressLineStep.SENT
                    && !(invoice.approvalState === approvalStep.CUSTOMER_APPROVED
                         || invoice.approvalState === approvalStep.CUSTOMER_REJECTED)"
                (click)="onCustomerReject()">
          <mat-icon>cancel</mat-icon>CUSTOMER REJECTED
        </button>
        <button mat-button class="fb-btn fb-primary"
                *ngIf="invoice.progressStep === progressLineStep.SENT
                    && !(invoice.approvalState === approvalStep.CUSTOMER_APPROVED
                         || invoice.approvalState === approvalStep.CUSTOMER_REJECTED)"
                (click)="onCustomerApprove()">
          <mat-icon>check_circle_outline</mat-icon>CUSTOMER APPROVED
        </button> -->
        <button mat-button class="fb-btn fb-danger" *ngIf="invoice.overdueState === overdueStep.OVERDUE"
                (click)="maskAsLost()">
          <mat-icon>cancel</mat-icon>MARK AS LOSS
        </button>
        <button mat-button class="fb-btn fb-danger" *ngIf="invoice.overdueState === overdueStep.OVERDUE"
                (click)="markAsSubtended()">
          <mat-icon>warning_amber</mat-icon>MARK AS SUBSTANDARD
        </button>
        <button mat-button class="fb-btn fb-danger" *ngIf="invoice.overdueState === overdueStep.OVERDUE"
                (click)="markAsDoubtful()">
          <mat-icon>help_outline</mat-icon>MARK AS DOUBTFUL
        </button>
      </ng-container>
      <button mat-button class="fb-btn fb-secondary" (click)="changeCurrency()">
        <mat-icon>swap_horiz</mat-icon>CONVERT
      </button>
    </div>
  </div>

  <ng-container *ngIf="openedFromSentInvoices && (invoice$ | async) as invoice">
    <div class="ir-chat-overlay" *ngIf="chatOpen" (click)="closeChat()"></div>
    <hmt-invoice-chat-panel
      *ngIf="chatOpen"
      [invoice]="invoice"
      [supplierOrg]="supplierOrg"
      [customerOrg]="customerOrg"
      (close)="closeChat()">
    </hmt-invoice-chat-panel>
  </ng-container>
</div>
